import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import stateStorage from './stateStorage'
import {
  RegionCode,
  RemoteConfigState,
  remoteConfigStateSchema,
} from '@ca/common'
import AppConfig, { getStaticRegion } from '../config/AppConfig'
import { StrapiID } from '@ca/common/types'
import { useMemo } from 'react'

export type RemoteConfigParams = {
  region: string
  versionId?: StrapiID
  state: RemoteConfigState
}

type RemoteConfigStore = {
  params: RemoteConfigParams
  setVersionId: (versionId: StrapiID | undefined) => void
  setState: (state: RemoteConfigState) => void
  setRegion: (state: string) => void
  toggle: () => void
  reset: () => void
}

export const defaultRemoteConfigState: RemoteConfigState = AppConfig.isDev
  ? 'preview'
  : 'public'

const useRemoteConfigParams = create<
  RemoteConfigStore,
  [['zustand/persist', { params: RemoteConfigParams }]]
>(
  persist(
    (set) => ({
      params: {
        region: AppConfig.staticRegion.code,
        versionId: undefined,
        state: AppConfig.remoteConfig.state,
      },
      setVersionId: (versionId) =>
        set((s) => ({
          params: {
            ...s.params,
            versionId,
          },
        })),
      setState: (state: RemoteConfigState) =>
        set((s) => ({
          params: {
            ...s.params,
            state,
          },
        })),
      setRegion: (region: string) =>
        set((s) => ({ params: { ...s.params, region } })),
      toggle: () => {
        set((s) => ({
          params: {
            ...s.params,
            versionId: undefined,
            state: s.params.state === 'public' ? 'preview' : 'public',
          },
        }))
      },
      reset: () => {
        set((s) => ({
          params: {
            ...s.params,
            versionId: undefined,
            state: defaultRemoteConfigState,
          },
        }))
      },
    }),
    {
      name: 'remoteConfigParams',
      partialize: (state) => ({
        params: {
          region: state.params.region,
          state: state.params.state,
          versionId: undefined,
        },
      }),
      merge: (persisted: any, current) => {
        if (persisted.params.region !== current.params.region) {
          return current
        }
        const state = remoteConfigStateSchema.safeParse(persisted.params.state)
        return {
          ...current,
          ...persisted,
          state: state.success ? state.data : 'public',
        }
      },
      storage: createJSONStorage(() => stateStorage),
    }
  )
)

const getRegion = (code?: RegionCode) => {
  if (!code) {
    return AppConfig.staticRegion
  }

  if (code === AppConfig.staticRegion.code) {
    return AppConfig.staticRegion
  }

  return getStaticRegion(code) || AppConfig.staticRegion
}

export const useAppRegion = () => {
  const region = useRemoteConfigParams((s) => s.params.region)
  return useMemo(() => getRegion(region as RegionCode), [region])
}

export const getAppRegion = () => {
  const code = useRemoteConfigParams.getState().params.region
  return getRegion(code as RegionCode)
}

export default useRemoteConfigParams
